import { Link } from "react-router-dom";
import { Sparkles, Flame, Route, Bot, Plane, Zap, BookOpen, Award, Briefcase, LogIn, ClipboardList, Gauge } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { getLocalLabs } from "@/lib/smart";
import { LAB_META, TIER, fmtTime } from "@/lib/labs";
import { Stars } from "@/components/smart/lab/MissionHost";
import { isStudent } from "@/lib/classroom_live";

const TRACKS = [["robotics", "Robotics", "रोबोटिक्स", Bot], ["drones", "Drones", "ड्रोन", Plane], ["ai", "AI", "AI", Sparkles]];
const LEVEL = { beginner: ["Beginner", "शुरुआती"], intermediate: ["Intermediate", "मध्यम"], advanced: ["Advanced", "एडवांस"] };
const KIND_ICON = { lesson: BookOpen, lab: Bot, challenge: Zap };

const StageCard = ({ p }) => {
  const { L } = useLang();
  return (
    <div className="rounded-3xl bg-[#0A192F] text-white p-6" data-testid="smart-stage">
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">{L("Skill stage", "स्किल स्टेज")}</div>
      <div className="mt-1 flex items-end justify-between gap-3">
        <div className="text-3xl font-black" data-testid="smart-stage-name">{L(p.stage, p.stage_hi)}</div>
        <div className="text-right"><div className="text-2xl font-black text-[#00E5FF]" data-testid="smart-xp">{p.xp}</div><div className="text-[11px] text-slate-300">XP</div></div>
      </div>
      <div className="mt-4 h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#00E5FF] to-[#FF2E63]" style={{ width: `${p.stage_pct}%` }} data-testid="smart-stage-bar" /></div>
      <div className="mt-1.5 text-[11px] text-slate-300">{p.next_stage ? L(`${p.next_xp - p.xp} XP to ${p.next_stage}`, `${p.next_stage_hi} तक ${p.next_xp - p.xp} XP`) : L("Top stage reached!", "सबसे ऊँची स्टेज!")}</div>
      <ol className="mt-5 grid grid-cols-5 gap-1.5" data-testid="smart-stage-ladder">
        {p.stages.map((s, i) => (
          <li key={s.name} className={`rounded-xl px-1 py-2 text-center text-[10px] font-bold leading-tight ${i <= p.stage_index ? "bg-[#00E5FF] text-[#0A192F]" : "bg-white/10 text-slate-300"}`}>{L(s.name, s.name_hi)}</li>
        ))}
      </ol>
      <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold">
        <span className="px-2.5 py-1 rounded-full bg-white/10 inline-flex items-center gap-1" data-testid="smart-level"><Gauge className="w-3.5 h-3.5" />{L(...LEVEL[p.level])}</span>
        <span className="px-2.5 py-1 rounded-full bg-white/10 inline-flex items-center gap-1" data-testid="smart-streak"><Flame className="w-3.5 h-3.5 text-[#FFC94D]" />{L(`${p.streak}-day streak`, `${p.streak} दिन लगातार`)}</span>
      </div>
    </div>
  );
};

const TracksCard = ({ p }) => {
  const { L } = useLang();
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-6" data-testid="smart-tracks">
      <div className="font-extrabold text-[#0A192F]">{L("Your tracks", "आपके ट्रैक")}</div>
      <div className="mt-4 space-y-3">
        {TRACKS.map(([k, en, h, Icon]) => (
          <div key={k} data-testid={`smart-track-${k}`}>
            <div className="flex items-center justify-between text-sm font-semibold"><span className="inline-flex items-center gap-1.5"><Icon className="w-4 h-4 text-[#FF2E63]" />{L(en, h)}</span><span className="text-slate-500">{p.tracks[k]}%</span></div>
            <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#00B8D4] rounded-full" style={{ width: `${p.tracks[k]}%` }} /></div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-sm text-slate-600 rounded-2xl bg-[#F0F4F8] p-3" data-testid="smart-pace">{L(p.pace, p.pace_hi)}</p>
    </div>
  );
};

const PathCard = ({ p, onGo }) => {
  const { L } = useLang();
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-6" data-testid="smart-path">
      <div className="font-extrabold text-[#0A192F] inline-flex items-center gap-2"><Route className="w-5 h-5 text-[#FF2E63]" />{L("Your learning path", "आपका सीखने का रास्ता")}</div>
      <p className="text-xs text-slate-500">{L("Changes with your quiz scores, labs and challenges.", "क्विज़, लैब और चैलेंज के हिसाब से बदलता है।")}</p>
      {p.path.length === 0 ? <p className="mt-4 text-sm text-slate-600" data-testid="smart-path-empty">{L("All caught up! Revise any lesson or try the drone lab again.", "सब पूरा! कोई भी पाठ दोहराइए या ड्रोन लैब फिर से कीजिए।")}</p> : (
        <ol className="mt-4 space-y-2.5">
          {p.path.map((x, i) => {
            const Icon = KIND_ICON[x.kind];
            return (
              <li key={`${x.kind}-${x.target}-${i}`}>
                <button onClick={() => onGo(x.kind, x.target)} data-testid={`smart-path-item-${i}`}
                  className="w-full text-left flex items-center gap-3 rounded-2xl border border-slate-200 p-3 hover:border-[#00B8D4] transition-colors">
                  <span className="w-10 h-10 rounded-xl bg-[#E8F0FE] grid place-items-center shrink-0"><Icon className="w-5 h-5 text-[#007A8A]" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold text-[#0A192F] line-clamp-1">{L(x.title, x.title_hi)}</span>
                    <span className="block text-xs text-slate-500 line-clamp-1">{L(x.why, x.why_hi)}{x.due_date ? L(` · due ${x.due_date}`, ` · ${x.due_date} तक`) : ""}</span>
                  </span>
                  {x.assigned && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFE4EC] text-[#A3123E] inline-flex items-center gap-1" data-testid={`smart-path-assigned-${i}`}><ClipboardList className="w-3 h-3" />{L("Trainer", "ट्रेनर")}</span>}
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

const LabItem = ({ x, i }) => {
  const { L } = useLang();
  const meta = LAB_META[x.lab];
  const tier = TIER[Math.min(3, x.mission_stars || 0)];
  const Icon = meta?.icon || Bot;
  return (
    <li className="rounded-2xl border border-slate-200 p-4" data-testid={`portfolio-item-${i}`}>
      <div className="flex items-center justify-between gap-2 text-sm font-bold text-[#0A192F]"><span className="inline-flex items-center gap-1.5"><Icon className="w-4 h-4 text-[#FF2E63]" />{L(x.title, x.title_hi)}</span>
        <span className="text-[11px] text-slate-500 font-semibold">{(x.done_at || "").slice(0, 10)}</span></div>
      {meta && (
        <div className="mt-2 flex items-center gap-3" data-testid={`portfolio-lab-badge-${i}`}>
          <span className="w-11 h-11 rounded-full grid place-items-center text-white shadow-inner shrink-0" style={{ background: `radial-gradient(circle at 30% 30%, #ffffff66, ${tier[2]})` }}><Award className="w-5 h-5" /></span>
          <span className="min-w-0">
            <span className="block text-xs font-extrabold text-[#0A192F]">{L(meta.badge[0], meta.badge[1])} · <span style={{ color: tier[2] }}>{L(tier[0], tier[1])}</span></span>
            <span className="flex items-center gap-2 text-[11px] text-slate-500"><Stars n={x.mission_stars || 0} size="w-3.5 h-3.5" />{L("Best", "बेस्ट")} {fmtTime(x.mission_best ?? null)}</span>
          </span>
        </div>
      )}
      <div className="mt-1 text-xs text-slate-500">{L(`Checkpoints ${x.checks_ok ?? 0}/${x.checks_total ?? 0} · mistakes ${x.mistakes ?? 0}`, `चेकपॉइंट ${x.checks_ok ?? 0}/${x.checks_total ?? 0} · ग़लतियाँ ${x.mistakes ?? 0}`)}{x.config?.props ? ` · props ${x.config.props}` : ""}</div>
      {x.reflection && <p className="mt-2 text-sm text-slate-700 italic" data-testid={`portfolio-reflection-${i}`}>“{x.reflection}”</p>}
    </li>
  );
};

const Portfolio = ({ p }) => {
  const { L } = useLang();
  const items = p.portfolio || Object.entries(getLocalLabs()).filter(([, v]) => v.done)
    .map(([k, v]) => ({ kind: "lab", lab: k, title: LAB_META[k]?.title[0], title_hi: LAB_META[k]?.title[1], ...v }));
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-6" data-testid="smart-portfolio">
      <div className="font-extrabold text-[#0A192F] inline-flex items-center gap-2"><Briefcase className="w-5 h-5 text-[#FF2E63]" />{L("My portfolio", "मेरा पोर्टफ़ोलियो")}</div>
      {items.length === 0 ? <p className="mt-3 text-sm text-slate-500" data-testid="portfolio-empty">{L("Finish a Robot Lab or earn a Class Star badge and it shows up here.", "कोई रोबोट लैब पूरी करें या क्लास स्टार बैज पाएँ, वो यहाँ दिखेगा।")}</p> : (
        <ul className="mt-4 grid sm:grid-cols-2 gap-3">
          {items.map((x, i) => x.kind === "lab" ? <LabItem key={i} x={x} i={i} /> : (
            <li key={i} className="rounded-2xl border border-[#FFC94D] bg-[#FFF8E6] p-4" data-testid={`portfolio-item-${i}`}>
              <div className="text-sm font-bold text-[#0A192F] inline-flex items-center gap-1.5"><Award className="w-4 h-4 text-[#B45309]" />{L(`Class Star · Lesson ${x.lesson_n}`, `क्लास स्टार · पाठ ${x.lesson_n}`)}</div>
              <div className="mt-1 text-xs text-slate-600">{L(x.title, x.title_hi)}</div>
              <Link to={`/verify-star/${x.badge_no}`} className="mt-2 inline-block text-xs font-bold text-[#C81E4B]" data-testid={`portfolio-badge-link-${i}`}>{x.badge_no}</Link>
            </li>
          ))}
        </ul>
      )}
      {!isStudent() && <Link to="/student/login" className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#0A192F] text-white text-sm font-bold" data-testid="portfolio-login-btn"><LogIn className="w-4 h-4" />{L("Log in to keep your portfolio", "पोर्टफ़ोलियो सेव करने के लिए लॉग इन करें")}</Link>}
    </div>
  );
};

export const SmartProgress = ({ profile, onGo }) => {
  const { L } = useLang();
  if (profile === false) return <p className="text-sm text-[#C81E4B]" data-testid="smart-progress-error">{L("Couldn't load your progress. Try again.", "प्रगति लोड नहीं हुई। फिर कोशिश करें।")}</p>;
  if (!profile) return <div className="h-64 rounded-3xl bg-white/70 animate-pulse" data-testid="smart-progress-loading" />;
  return (
    <div className="space-y-5" data-testid="smart-progress">
      {profile.summary && <p className="rounded-2xl bg-[#E0FAFF] border border-[#00B8D4]/40 p-4 text-sm text-[#0A192F]" data-testid="smart-summary">{L(profile.summary, profile.summary_hi)}</p>}
      <div className="grid lg:grid-cols-3 gap-5">
        <StageCard p={profile} />
        <TracksCard p={profile} />
        <PathCard p={profile} onGo={onGo} />
      </div>
      <Portfolio p={profile} />
    </div>
  );
};
